import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { stateForTrack } from "@/utils/race-results";

async function fetchResultsDay(iso) {
  const res = await fetch(
    `/api/public-race-results?date=${encodeURIComponent(iso)}`,
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error || `Couldn't load results (${res.status})`);
  }
  return res.json();
}

function useTrackDirectory() {
  return useQuery({
    queryKey: ["track-names-directory"],
    queryFn: async () => {
      const res = await fetch("/data/horseracing/tracks/track-names.json");
      if (!res.ok) throw new Error("Failed to load track metadata");
      return res.json();
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/**
 * Public results for a list of days (one cached request per day).
 * Returns [{ iso, status, error, refetch, races }] in the same order as `dates`,
 * with each race's state filled in from the track directory.
 */
export function useRaceResultsDays(dates) {
  const { data: directory } = useTrackDirectory();
  const queries = useQueries({
    queries: dates.map((iso) => ({
      queryKey: ["public-race-results", iso],
      queryFn: () => fetchResultsDay(iso),
      staleTime: 30 * 60 * 1000,
      gcTime: 2 * 60 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    })),
  });

  const signature = queries
    .map((q) => `${q.status}:${q.dataUpdatedAt}`)
    .join("|");
  return useMemo(
    () =>
      dates.map((iso, i) => {
        const q = queries[i];
        return {
          iso,
          status: q?.status ?? "pending",
          error: q?.error ?? null,
          refetch: q?.refetch,
          races: (q?.data?.races || []).map((race) => ({
            ...race,
            state: stateForTrack(race.track, directory),
          })),
        };
      }),
    // queries is a new array every render; the signature tracks real changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dates.join(","), signature, directory],
  );
}
